import { useState, createContext, useCallback, useMemo } from 'react';
import * as ivs from 'amazon-ivs-player';
import wasmBinaryPath from 'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm?url';
import wasmWorkerPath from 'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js?url';

const createAbsolutePath = (assetPath: string) => new URL(assetPath, document.URL).toString();

const ResourcePoolContext = createContext<ResourcePool | null>(null);

export { ResourcePoolContext };

// core is a structure added on during the ivs create
// function that isn't typed in the amazon player for
// some weird reason, so extending here.
export interface IvsMediaPlayer extends ivs.MediaPlayer {
  core?: {
    id: number;
  };
}
export interface ObjectPoolMember {
  data: IvsMediaPlayer;
  free: boolean;
  index: number;
}

export interface ResourcePool {
  getElement: () => ObjectPoolMember;
  releaseElement: (elementIndex: number) => void;
}

const useResourcePool = (): ResourcePool => {
  const [ivsPlayerPool, setIvsPlayerPool] = useState<Array<ObjectPoolMember>>([]);

  const createElement = useCallback(() => {
    if (!wasmWorkerPath || !wasmBinaryPath) {
      throw new Error('Worker or binary path not found');
    }

    const ivsPlayer = ivs.create({
      wasmWorker: createAbsolutePath(wasmWorkerPath),
      wasmBinary: createAbsolutePath(wasmBinaryPath),
    }) as IvsMediaPlayer;

    const newElement: ObjectPoolMember = {
      data: ivsPlayer,
      free: true,
      index: ivsPlayer?.core?.id || 0,
    };
    return newElement;
  }, []);

  const getElement = useCallback((): ObjectPoolMember => {
    for (let i = 0; i < ivsPlayerPool!.length; i += 1) {
      if (ivsPlayerPool?.[i]?.free) {
        ivsPlayerPool[i].free = false;
        return ivsPlayerPool[i];
      }
    }
    const newObject = createElement();
    newObject.free = false;
    setIvsPlayerPool((prevPool) => [...prevPool, newObject]);
    return newObject;
  }, [createElement, ivsPlayerPool]);

  const releaseElement = useCallback((elementIndex: number) => {
    if (elementIndex || elementIndex === 0) {
      setIvsPlayerPool((prevPool) =>
        prevPool.map((elm) => {
          if (elm.data.core?.id === elementIndex) {
            elm.data.pause();
            return {
              data: elm.data,
              free: true,
              index: elementIndex,
            };
          }
          return elm;
        })
      );
    }
  }, []);

  return useMemo(() => {
    return {
      getElement,
      releaseElement,
    };
  }, [getElement, releaseElement]);
};

export default useResourcePool;
